// Teams directory — sourced from Mongo via the Symbols SDK, not from
// Supabase. The `user_profiles.department` / `user_profiles.group_name`
// text columns on governance are legacy; team membership is now
// canonical in Mongo's `Team` + `TeamMember`.
//
// Load once per session into root state:
//   root.teams         Array of { id, name, slug, parentTeam, orgId }
//   root.teamsByEmail  { [lowercase email]: Array<{ id, name, role }> }
//
// Consumers (peopleGroups, PersonCard, MemberProfile, _person mapper)
// read from root.teamsByEmail instead of person.department / .group.
//
// Moved from workspace/functions/teams.js (2026-08-15, MemberProfile
// extraction — tickets/opus.md) so MemberProfile can live in `shared` and
// stay reachable from every host (workspace, canvas). Self-contained
// already (its only dependency was the shared SDK singleton) — no other
// behavior change. workspace/functions/teams.js now re-exports this file
// so its other consumers (peopleGroups.js, PersonCard.js,
// loadPrivateData.js) are unaffected.
import { getSDK } from './sdk.js'

// SDK list-response unwrap — the server returns either `{items}`,
// `{data:{items}}`, `{data:[...]}`, or a bare array depending on the
// endpoint + version. Normalize to a plain array.
const _unwrapList = (r) => r?.items || r?.data?.items || r?.data || (Array.isArray(r) ? r : [])

// Stable non-PII join key off a roster row. The server now stamps a
// top-level `userId` on every row for all viewers (memberProjection.js);
// fall back to the populated `user._id` for older payloads. This is the key
// a non-mgmt viewer's membership resolves on — their rows carry no email
// (tickets/fable.md P0 regression).
const _memberUserId = (m) => {
  const raw = m?.userId != null ? m.userId : m?.user?._id != null ? m.user._id : m?.user?.id
  return raw != null ? String(raw) : ''
}

// Every id-space a person can be joined on: the canonical Mongo userId AND
// the Supabase UUID alias when the server sends one. The people roster keys
// identity as `supabaseUserId || _id`, so a federated peer's people-row id
// is their Supabase UUID while their team-row userId is the Mongo _id —
// index the membership under BOTH so a consumer holding EITHER id resolves
// it (tickets/fable.md P0 regression). Deduped; empty when neither exists.
const _memberIdKeys = (m) => {
  const keys = []
  const uid = _memberUserId(m)
  if (uid) keys.push(uid)
  const sb = m?.supabaseUserId != null ? String(m.supabaseUserId) : ''
  if (sb && sb !== uid) keys.push(sb)
  return keys
}

// Use the shared SDK instance (pointed at the same channel as the rest of
// the workspace — `localhost:8080` in localNext, `next.api.symbols.app`
// in next, etc.). The previous hack here built its own SDK and copied
// the federation bridge token into `symbols_access_token`, which broke
// `/core/*` calls in localNext mode (bridge token is signed by prod, the
// local server uses a different secret). The shared SDK already carries
// the locally-minted token via TokenManager — no per-module override.
const _getSdk = () => {
  // Test seam — same shape as workspaceProjects.js's `__setModuleSdkFnForTests`
  // family. `getSDK()` is a process-wide singleton behind a cache keyed on the
  // resolved channel URL, so a unit test cannot substitute it without writing
  // globals that leak into every LATER file of the same bun process (one bun
  // process runs a whole package). An explicit, named seam that the test
  // clears in its own teardown is the honest alternative.
  // FA201: the handle lives on globalThis, never a module-scope `let`.
  const injected = globalThis.__teamsSdkForTests
  if (typeof injected === 'function') return injected()
  if (typeof window === 'undefined') return null
  return getSDK()
}

// Install / remove the seam above. `null` restores the real singleton.
export const __setTeamsSdkForTests = (fn) => {
  globalThis.__teamsSdkForTests = typeof fn === 'function' ? fn : null
}

// In-flight dedup + short-TTL cache for loadAllTeams (2026-06-08 perf).
// loadAllTeams fans out 2 requests PER ORG across every org the user
// belongs to (listOrgMembers + listTeams). On /studio it was fired ~3×
// concurrently — loadPrivateData (sign-in), onWorkspaceChange
// (org-resolve), and the page's own data load — with no dedup, so a
// 6-org user issued ~36 simultaneous /core/organizations/:id/{teams,
// members} requests that saturated the server (~4s each) and chat /
// member surfaces waited 5–9s. The guard collapses concurrent callers
// onto ONE fan-out and serves a fresh result to callers arriving within
// the TTL, cutting ~36 → 12 requests. Keyed by the sorted org-id set so
// an org switch (different claims) misses the cache and reloads; the
// switch also wipes the relevant root state in onWorkspaceChange.
// FA201 / frank trap (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1) — the two
// handles used to be module-scope `let`s (`_allTeamsInflight`,
// `_allTeamsCache`) read by the EXPORTED `loadAllTeams`. frank hoists that
// export into the emitted `globalScope` and revives it with a bare indirect
// eval, where nothing module-level is in scope, so each read emitted a
// guaranteed ReferenceError and frank's emission gate refused the whole
// publish (`PUBLISH BLOCKED — [unresolvable-identifier]`) for any project that
// inlines this package AND uses this export. Re-homed onto
// `globalThis.__allTeamsState`, per the pointerCapability.js template; the
// lazy create is INLINED at the one read site, because a shared module-scope
// getter would just relocate the same bare-sibling-call hazard.
//
// The tenant keying is UNCHANGED: the sorted org-id set still lives INSIDE
// each value (`{ key, … }`) and is still compared before reuse, so this is
// the same TENANT-KEYED shape the module-state census classified — only its
// home moved.
const _ALL_TEAMS_TTL_MS = 5000

// Pull every team across every team-org the caller belongs to, plus
// the members of each team AND each org's full member roster. Returns:
//   teams          Array<{ id, name, slug, parentTeam, orgId, orgName }>
//   teamsByEmail   { [email]: Array<{ id, name, slug, orgId, role }> }
//   orgMembersByOrgId  { [orgId]: Set<email> }
//
// `orgMembersByOrgId` is what /people, mention lists, and other
// user-scoped surfaces use to filter the cross-tenant `public.people`
// view down to the active org's membership. Avoids a schema change on
// Supabase's identity tables.
// Public entry — dedups concurrent callers and serves a short-TTL cache
// (see `globalThis.__allTeamsState` above). The actual fan-out is
// in _loadAllTeamsUncached.
export const loadAllTeams = async (root, opts) => {
  // SCOPE. By default this loads the ACTIVE (scoped) organisation ONLY — see
  // the ACTIVE-ORG-ONLY note in _loadAllTeamsUncached. `{ allOrgs: true }` is
  // the opt-in fan-out across every org in the claim set, for a surface that
  // genuinely shows more than one org at a time. The cache key carries the
  // scope so the two answers can never be served for each other.
  const allOrgs = !!(opts && opts.allOrgs)
  const orgIds = (root?.jwtClaims?.orgs || [])
    .map((o) => o?.id)
    .filter(Boolean)
    .sort()
  const key = orgIds.length ? (allOrgs ? 'all:' : 'active:') + orgIds.join(',') : ''

  if (!globalThis.__allTeamsState) {
    globalThis.__allTeamsState = { inflight: null, cache: null, denied: null }
  }
  const state = globalThis.__allTeamsState

  // Fresh-enough cached result → reuse (collapses re-fires within the
  // page-settle window). Empty key (no orgs yet) is never cached.
  if (
    key &&
    state.cache &&
    state.cache.key === key &&
    Date.now() - state.cache.ts < _ALL_TEAMS_TTL_MS
  ) {
    return state.cache.result
  }

  // A fan-out for this exact org set is already running → await it
  // instead of starting a second identical burst.
  if (key && state.inflight && state.inflight.key === key) {
    return state.inflight.promise
  }

  const promise = _loadAllTeamsUncached(root, allOrgs)
  if (key) state.inflight = { key, promise }
  try {
    const result = await promise
    if (key) state.cache = { key, result, ts: Date.now() }
    return result
  } finally {
    if (state.inflight && state.inflight.promise === promise) {
      state.inflight = null
    }
  }
}

const _loadAllTeamsUncached = async (root, allOrgs) => {
  const sdk = await _getSdk()
  if (!sdk) return { teams: [], teamsByEmail: {}, teamsByUserId: {}, orgMembersByOrgId: {} }
  // Fetch members for EVERY org the user belongs to. Filtering by
  // type (`'team'` only) used to skip the Symbols System home org on
  // some accounts — when the active workspace lives under such an
  // org, /people then renders empty.
  const orgs = root?.jwtClaims?.orgs || []
  const teams = []
  const teamsByEmail = {}
  const orgMembersByOrgId = {}

  // Parallel across orgs — 2 calls per org (listOrgMembers + listTeams)
  // and N orgs gives 2N concurrent requests. Well under the 180 req/min
  // free-tier cap for any realistic N. The earlier serial loop predates
  // the deferred `loadTeamMembers` and was guarding against an N×teams
  // burst that no longer fires from here.
  //
  // PERF-3: within each org, listOrgMembers and listTeams are independent
  // API calls — fire them in parallel via Promise.all. For a user in K
  // orgs this halves the per-org wall-clock from 2 serial RTTs to 1
  // concurrent RTT. No order dependency between the two calls: the member
  // map only feeds orgMembersByOrgId (not consumed by teams list), and
  // the teams list only feeds the `teams` accumulator.
  // Per-org fetch — members + teams accumulated into the shared maps.
  const processOrg = async (org) => {
    const orgId = org.id
    if (!orgId) return
    // AN ORG THAT ALREADY REFUSED US IS NEVER ASKED AGAIN THIS SESSION.
    // `/core/auth/me` can list an org whose own read answers 403
    // ("Organization membership required") — a stale claim, Mongo ↔ JWT
    // staleness. The catches below already swallow it into "no teams", but a
    // swallowed 403 is still a REQUEST: the browser logs `Failed to load
    // resource: 403` in the console on its own, and no JS can suppress that.
    // The only fix is not to ask twice. Measured on the Vault white-label host
    // 2026-09-09: two 403s on EVERY page load, for the workspace Owner.
    if (!globalThis.__allTeamsState) {
      globalThis.__allTeamsState = { inflight: null, cache: null, denied: null }
    }
    const _denied = globalThis.__allTeamsState.denied
    if (_denied && _denied[orgId]) return
    // A 403 on either call means the org is not readable by this session at
    // all — record it once, for both calls, and for every later pass.
    const _markDenied = (err) => {
      const msg = (err && err.message) || ''
      const status = err && (err.status || err.statusCode)
      if (status !== 403 && !/Organization membership required/i.test(msg)) return
      if (!globalThis.__allTeamsState.denied) globalThis.__allTeamsState.denied = {}
      globalThis.__allTeamsState.denied[orgId] = true
    }

    // Store orgMembers as a plain lookup object ({email: true}) not a
    // Set because smbls's root.update() path JSON-clones state and Sets
    // don't survive — entries become empty {}. Plain objects round-trip.
    const [oRes, teamsRes] = await Promise.all([
      sdk.listOrgMembers(orgId).catch((err) => {
        // "Organization membership required" is the SDK's response when
        // the JWT lists an org the user is no longer a member of (Mongo
        // ↔ Supabase JWT staleness — see tickets/sdk.md SDK-WORKSPACE-TEAMS-1).
        // The missing key downgrades the UI to "show full cross-tenant
        // list" via filterToActiveOrg. Demote this to debug to stop
        // spamming console on every page load.
        _markDenied(err)
        const msg = err?.message || ''
        if (!/Organization membership required/i.test(msg)) {
          console.warn('[teams] listOrgMembers failed for', orgId, err)
        }
        return null
      }),
      sdk.listTeams(orgId).catch((err) => {
        _markDenied(err)
        const msg = err?.message || ''
        if (!/Organization membership required/i.test(msg)) {
          console.warn('[teams] listTeams failed for', orgId, err)
        }
        return null
      })
    ])

    if (oRes !== null) {
      const oMembers = _unwrapList(oRes)
      const map = {}
      for (const m of oMembers) {
        const email = (m?.user?.email || m?.email || '').toLowerCase()
        if (email) map[email] = true
      }
      orgMembersByOrgId[orgId] = map
    }

    if (teamsRes === null) return
    const list = _unwrapList(teamsRes)
    // Per-team member fetching is intentionally deferred — each
    // listTeamMembers is a separate API call and with many teams
    // bursts past the free-tier cap (180 req/min). loadTeamMembers()
    // is called lazily when the user opens a team detail view.
    for (const t of list) {
      teams.push({
        id: t._id || t.id,
        name: t.name,
        slug: t.slug,
        parentTeam: t.parentTeam || null,
        category: t.category || 'active',
        isSystem: !!t.isSystem,
        permissions: t.permissions || [],
        orgId,
        orgName: org.name
      })
    }
  }

  // ACTIVE-ORG-FIRST (2026-06-08 per Nika "optimize general loading /
  // double-check how we handle requests"): fetch the ACTIVE org alone
  // first and commit its slice to root immediately, THEN background the
  // rest. The old `Promise.all(orgs.map(...))` fired all 2N requests (12
  // for a 6-org user) in a single burst that pegged the API and starved
  // the critical-path calls queued behind it (the dashboard's
  // loadOrgMembers, loadProjects) — that pile-up was the main reason
  // /studio members + projects loaded slowly. Active-first cuts the
  // opening burst to 2 requests, so the active-org views paint right away
  // and the freed capacity lets the rest of the page through. Falls back
  // to the all-at-once path when the active org isn't in the claim set.
  const activeOrgId = root?.jwtClaims?.workspace_org_id
  const active = activeOrgId && orgs.find((o) => o.id && o.id === activeOrgId)
  if (active) {
    await processOrg(active)
    try {
      root.update({ teams: [...teams], orgMembersByOrgId: { ...orgMembersByOrgId } })
    } catch (_) {}
    // ── ACTIVE-ORG-ONLY (2026-09-09, A2-04) ──────────────────────────────
    // Boot stops here. Every consumer of `root.teams` /
    // `root.orgMembersByOrgId` keys on the ACTIVE org — /admin/teams filters
    // to `jwtClaims.workspace_org_id`, filterToActiveOrg and
    // PersonDetailModal both index by the active org id — so the other orgs'
    // rows were fetched and never read. On a white-label host that is the
    // whole point: the custom domain scopes the session to ONE organisation.
    //
    // The cost of fetching them anyway was not only latency. Nika's account
    // lists 13 orgs and one of them answers 403 on its own read, so EVERY
    // page load put two 403s in the console of a customer-facing build, for
    // the workspace Owner (measured on vault.pr, 2026-09-09).
    //
    // A cross-org surface asks for the rest explicitly, with
    // `{ allOrgs: true }`, when the user opens it — never on the boot path.
    if (!allOrgs) return { teams, teamsByEmail, teamsByUserId: {}, orgMembersByOrgId }
  }
  // No active org in the claim set (or an explicit cross-org caller): the
  // original fan-out. Without this a session that cannot resolve its own
  // active org would get an EMPTY directory instead of a slow one.
  await Promise.all(orgs.filter((o) => o !== active).map(processOrg))

  return { teams, teamsByEmail, teamsByUserId: {}, orgMembersByOrgId }
}

// Lazy-fetch members for a single team. Merges results into
// root.teamsByEmail without stomping other teams' entries. Idempotent —
// a second call for the same teamId is skipped (root.teamMembersLoaded
// tracks which teams have been fetched this session).
//
// Usage: call from the detail view onInit / show transition.
export const loadTeamMembers = async (root, team) => {
  if (!team?.id) return
  // Skip if already loaded this session.
  if (root?.teamMembersLoaded?.[team.id]) return
  const sdk = await _getSdk()
  if (!sdk) return
  try {
    const mRes = await sdk.listTeamMembers(team.orgId, team.id)
    const members = _unwrapList(mRes)
    // Merge new entries into the existing maps without touching entries from
    // other teams (different teamId). Both indexes are written: teamsByUserId
    // (the stable non-PII key that rides EVERY row) is primary; teamsByEmail
    // is kept for consumers that only hold an email and for mgmt / optimistic
    // writes — but a non-mgmt viewer's rows carry NO email, so only the id
    // index resolves for them (tickets/fable.md P0 regression).
    const next = { ...(root?.teamsByEmail || {}) }
    const nextById = { ...(root?.teamsByUserId || {}) }
    for (const m of members) {
      const email = (m?.user?.email || m?.email || '').toLowerCase()
      const userId = _memberUserId(m)
      const idKeys = _memberIdKeys(m)
      if (!email && !idKeys.length) continue
      const entry = {
        id: team.id,
        name: team.name,
        slug: team.slug,
        category: team.category,
        orgId: team.orgId,
        // Permission-group flag (server SystemTeamsSeeder): Admin /
        // Manager / Members / HR / … are seeded per org as permission
        // carriers wearing the Team model. Surfaces that mean "real
        // team" (people grouping, teams rail) filter these out.
        isSystem: !!team.isSystem,
        // Person identity off the populated TeamMember.user — lets the
        // /team-members team filter render members who aren't in the
        // people roster (no user_profiles row yet) as ghost cards.
        userName: m?.user?.name || '',
        userAvatar: m?.user?.avatar || null,
        userId: userId || null,
        role: m?.role || 'member',
        // §unified-perms PR #1 — per-member overlay. Default 'active'
        // matches the TeamMember schema default; the server stamps
        // 'passive' explicitly when an admin elevates a member without
        // changing the team's category.
        status: m?.status || 'active'
      }
      // Avoid duplicate entries for this team on re-open. Index under every
      // id-space (Mongo + Supabase) so either kind of consumer id resolves.
      if (email) next[email] = [...(next[email] || []).filter((e) => e.id !== team.id), entry]
      for (const k of idKeys)
        nextById[k] = [...(nextById[k] || []).filter((e) => e.id !== team.id), entry]
    }
    root.update({
      teamsByEmail: next,
      teamsByUserId: nextById,
      teamMembersLoaded: { ...(root?.teamMembersLoaded || {}), [team.id]: true }
    })
  } catch (err) {
    console.warn('[teams] loadTeamMembers failed for', team.id, err)
  }
}

// Eagerly populate `root.teamsByEmail` for every team in `root.teams`.
// Without this, `teamsByEmail` stays empty until the user opens a team
// detail page — meaning /people groups every member into "Uncategorized"
// and /team-and-agents can't render team GroupFrames. Calls
// `loadTeamMembers` per team in parallel; each is idempotent via the
// `teamMembersLoaded` flag, so repeat invocations are cheap.
//
// Perf (2026-06-06): scoped to the ACTIVE org and batched.
// Previously this fanned out one listTeamMembers request PER TEAM
// ACROSS EVERY ORG the user belongs to (47 requests ≈ 3s each on
// /team-members). Every consumer (people grouping, team rails, T&A
// canvas) is active-org-scoped, and the org switch wipes + reloads
// teamsByEmail anyway — so other orgs' members were dead weight.
// The batched `listOrgTeamMembers` endpoint returns the whole org in
// one call; the per-team loop remains as a fallback for servers that
// don't ship the endpoint yet.
export const loadAllTeamMembers = async (root) => {
  const teams = root?.teams || []
  if (!teams.length) return
  const orgId = root?.jwtClaims?.workspace_org_id
  const orgTeams = orgId ? teams.filter((t) => String(t.orgId) === String(orgId)) : teams
  if (!orgTeams.length) return

  const sdk = await _getSdk()
  if (sdk && typeof sdk.listOrgTeamMembers === 'function' && orgId) {
    try {
      const { members = [] } = (await sdk.listOrgTeamMembers(orgId)) || {}
      const teamById = {}
      for (const t of orgTeams) teamById[String(t.id)] = t
      const next = { ...(root?.teamsByEmail || {}) }
      const nextById = { ...(root?.teamsByUserId || {}) }
      const loaded = { ...(root?.teamMembersLoaded || {}) }
      for (const m of members) {
        const team = teamById[String(m?.team)]
        if (!team) continue
        const email = (m?.user?.email || m?.email || '').toLowerCase()
        const userId = _memberUserId(m)
        const idKeys = _memberIdKeys(m)
        // A non-mgmt viewer's rows carry a userId but NO email — index them
        // by id so their memberships still resolve (fable.md P0 regression).
        if (!email && !idKeys.length) continue
        const entry = {
          id: team.id,
          name: team.name,
          slug: team.slug,
          category: team.category,
          orgId: team.orgId,
          isSystem: !!team.isSystem,
          userName: m?.user?.name || '',
          userAvatar: m?.user?.avatar || null,
          userId: userId || null,
          role: m?.role || 'member',
          status: m?.status || 'active'
        }
        if (email) next[email] = [...(next[email] || []).filter((e) => e.id !== team.id), entry]
        for (const k of idKeys)
          nextById[k] = [...(nextById[k] || []).filter((e) => e.id !== team.id), entry]
      }
      for (const t of orgTeams) loaded[t.id] = true
      root.update({ teamsByEmail: next, teamsByUserId: nextById, teamMembersLoaded: loaded })
      return
    } catch (err) {
      console.warn('[teams] listOrgTeamMembers failed — falling back to per-team:', err?.message)
    }
  }

  await Promise.all(orgTeams.map((t) => loadTeamMembers(root, t)))
}

// Convenience — the list of team entries a given person belongs to,
// SCOPED to the active org and deduped. `teamsByEmail` aggregates
// memberships across EVERY org the caller belongs to (loadAllTeams
// fans out per org), so the raw list carries same-named teams from
// other orgs — nika showed as [Members, Members, Members] (one per
// org) and groupings picked an arbitrary org's team. Falls back to
// the full (deduped) list when no active org is resolved.
// Case-insensitive by design; call sites pass either `r.email` or a
// freshly-typed string.
export const personTeams = (personOrEmail, root) => {
  // Accept a person-like `{ userId, email }` OR a bare email string (legacy
  // callers). `userId` is the PRIMARY join key — the server stamps it on
  // EVERY roster row for every viewer (memberProjection.js), whereas a
  // non-mgmt viewer's rows carry NO email, so teamsByEmail is empty for them
  // and only the userId index resolves (tickets/fable.md P0 regression).
  // Email is the fallback for legacy callers and mgmt-viewer rows.
  const p = typeof personOrEmail === 'string' ? { email: personOrEmail } : personOrEmail || {}
  const uid = p.userId != null ? String(p.userId) : ''
  const key = String(p.email || '').toLowerCase()
  const byId = (uid && root?.teamsByUserId?.[uid]) || null
  const byEmail = (key && root?.teamsByEmail?.[key]) || null
  const raw = byId && byId.length ? byId : byEmail || byId || []
  const orgId = root?.jwtClaims?.workspace_org_id
  const scoped = orgId ? raw.filter((t) => String(t?.orgId) === String(orgId)) : raw
  const seen = new Set()
  const out = []
  for (const t of scoped) {
    const k = String(t?.id || t?.name || '')
    if (!k || seen.has(k)) continue
    seen.add(k)
    out.push(t)
  }
  return out
}

// Primary team label for a person — first REAL (non-system) team entry,
// or null. System permission-carrier teams (Admin / Manager / Members /
// HR / …, seeded per org by SystemTeamsSeeder) are auto-assigned to every
// org member; treating one as "my team" made a person with NO real team
// assignment display the same team name every other member shares — which
// directly contradicted the People directory's "No team" bucket for the
// same person (peopleGroups.js's `_realTeams` already excludes system
// teams from grouping; this brings the single-name consumers — the
// MemberProfile Team row — in line with that, see tickets/sonnet.md
// TEAM-GROUPING-1). Consumers that need a single string (row badges,
// subtitles, profile Team row) call this.
export const primaryTeamName = (personOrEmail, root) => {
  const real = personTeams(personOrEmail, root).filter((t) => !t.isSystem)
  return real.length ? real[0].name : null
}

// ── Write helpers ─────────────────────────────────────────────────
// Thin wrappers around SDK methods. Optimistically update
// root.teamsByEmail on success so the UI reflects the change before
// the next `loadAllTeams()` refresh.

// Generic optimistic indexer — appends one team entry under an arbitrary
// key (a lowercased email for teamsByEmail, a userId for teamsByUserId).
// No-op when the key is empty (e.g. a non-mgmt caller has no peer email).
const _addTeamEntry = (map, key, team, role = 'member') => {
  if (!key) return map || {}
  // Mirror the shape loadAllTeamMembers writes — category + status feed
  // PersonDetailModal's effective-perms calc and the category-coloring
  // on TeamPersonNode. Omitting them made every optimistic add visibly
  // revert to "unknown" until the next refresh.
  const entry = {
    id: team.id,
    name: team.name,
    slug: team.slug,
    orgId: team.orgId,
    role,
    category: team.category || null,
    status: team.category === 'passive' ? 'passive' : 'active'
  }
  const base = map || {}
  return { ...base, [key]: [...(base[key] || []), entry] }
}

const _removeTeamEntry = (map, key, teamId) => {
  if (!key) return map || {}
  const next = { ...(map || {}) }
  next[key] = (next[key] || []).filter((t) => t.id !== teamId)
  if (!next[key].length) delete next[key]
  return next
}

// Add a user to a team. Updates root.teamsByEmail optimistically.
export const addMemberToTeam = async (root, { orgId, teamId, userId, email, role = 'member' }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  await sdk.addTeamMember(orgId, teamId, { userId, role })
  const team = (root?.teams || []).find((t) => t.id === teamId) || {
    id: teamId,
    name: '',
    slug: '',
    orgId
  }
  // Write BOTH indexes optimistically — teamsByUserId (the stable non-PII key
  // that resolves for every viewer) and teamsByEmail (mgmt / legacy callers).
  root.update({
    teamsByEmail: _addTeamEntry(
      root.teamsByEmail || {},
      String(email || '').toLowerCase(),
      team,
      role
    ),
    teamsByUserId: _addTeamEntry(
      root.teamsByUserId || {},
      userId != null ? String(userId) : '',
      team,
      role
    )
  })
}

// §unified-perms PR #13 — flip a TeamMember's status overlay
// ('active' ⇄ 'passive'). Resolves the TeamMember.id via a fresh
// listTeamMembers (no client-side cache), patches the row, and
// optimistically merges the new status into root.teamsByEmail so the
// PersonDetailModal effective-perms calc reflects the change before
// the next reload. Server-side validates the canManageTeam gate.
export const setTeamMemberStatus = async (root, { orgId, teamId, userId, email, status }) => {
  if (!['active', 'passive'].includes(status)) {
    throw new Error('status must be active | passive')
  }
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const mRes = await sdk.listTeamMembers(orgId, teamId)
  const members = _unwrapList(mRes)
  const row = members.find(
    (m) =>
      m?.user?.id === userId ||
      m?.user?._id === userId ||
      m?.userId === userId ||
      String(m?.user?.email || '').toLowerCase() === String(email || '').toLowerCase()
  )
  const memberId = row?.id || row?._id
  if (!memberId) throw new Error('Team member not found')
  await sdk.updateTeamMember(orgId, teamId, memberId, { status })
  // Optimistic merge: rewrite this team's row for this person in BOTH
  // indexes (userId resolves for every viewer, email for mgmt / legacy).
  const patchList = (list) => (list || []).map((e) => (e.id === teamId ? { ...e, status } : e))
  const key = String(email || '').toLowerCase()
  const uid = userId != null ? String(userId) : ''
  const upd = {}
  if (key) {
    const prev = root?.teamsByEmail || {}
    upd.teamsByEmail = { ...prev, [key]: patchList(prev[key]) }
  }
  if (uid) {
    const prevById = root?.teamsByUserId || {}
    upd.teamsByUserId = { ...prevById, [uid]: patchList(prevById[uid]) }
  }
  if (Object.keys(upd).length) root.update(upd)
}

// Remove a user from a team. Looks up the TeamMember id via a fresh
// `listTeamMembers` (no SDK-side membership cache). Updates
// root.teamsByEmail optimistically.
export const removeMemberFromTeam = async (root, { orgId, teamId, userId, email }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const mRes = await sdk.listTeamMembers(orgId, teamId)
  const members = _unwrapList(mRes)
  const row = members.find(
    (m) => m?.user?.id === userId || m?.user?._id === userId || m?.userId === userId
  )
  const memberId = row?.id || row?._id
  if (memberId) {
    await sdk.removeTeamMember(orgId, teamId, memberId)
  }
  // Drop the entry from BOTH indexes so the optimistic removal reflects for
  // every viewer tier (email-index for mgmt, userId-index for everyone).
  root.update({
    teamsByEmail: _removeTeamEntry(
      root.teamsByEmail || {},
      String(email || '').toLowerCase(),
      teamId
    ),
    teamsByUserId: _removeTeamEntry(
      root.teamsByUserId || {},
      userId != null ? String(userId) : '',
      teamId
    )
  })
}

// Create a new team in an org. Returns the created team doc. Also
// appends it to root.teams so the picker's option list updates without
// a full reload.
export const createTeam = async (root, { orgId, name }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const slug = String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  const res = await sdk.createTeam(orgId, { name, slug })
  const created = res?.data || res
  const team = {
    id: created._id || created.id,
    name: created.name,
    slug: created.slug,
    parentTeam: created.parentTeam || null,
    category: created.category || 'active',
    isSystem: !!created.isSystem,
    permissions: created.permissions || [],
    orgId,
    orgName: (root?.jwtClaims?.orgs || []).find((o) => o.id === orgId)?.name || ''
  }
  root.update({ teams: [...(root?.teams || []), team] })
  return team
}

// Compose `createTeam` + per-member `addMemberToTeam`. Used by the
// /team-and-agents Suggest team mode (creates a team from a node
// selection) and any future "create with seeded members" UI.
// `members` is an array of `{ userId, email }`; agents (no Mongo user
// row) should be filtered out by the caller before passing in.
//
// Best-effort: per-member errors are collected and returned so the UI
// can surface a partial-failure message ("Team created — 2 members
// failed to add"). A failure to create the team itself throws.
//
// Returns `{ team, errors }`.
export const createTeamWithMembers = async (root, { orgId, name, members = [] }) => {
  const team = await createTeam(root, { orgId, name })
  if (!team?.id) throw new Error('createTeam returned no id')
  if (members.length === 0) return { team, errors: [] }
  const errors = []
  await Promise.all(
    members.map(async (m) => {
      try {
        await addMemberToTeam(root, {
          orgId,
          teamId: team.id,
          userId: m.userId,
          email: m.email
        })
      } catch (err) {
        errors.push({ email: m.email, error: err?.message || String(err) })
      }
    })
  )
  return { team, errors }
}

// Update the permission set for a team. Hits PATCH /organizations/:orgId
// /teams/:teamId with { permissions: [key, ...] }. Server enforces
// admin.team_permissions (or owner-bypass). Optimistically writes the
// new permissions onto the team row in root.teams so the UI reflects
// the change before the next loadAllTeams.
export const updateTeamPermissions = async (root, { orgId, teamId, permissions }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const res = await sdk.updateTeam(orgId, teamId, { permissions })
  const updated = res?.data || res
  const nextTeams = (root?.teams || []).map((t) =>
    t.id === teamId ? { ...t, permissions: updated?.permissions || permissions } : t
  )
  root.update({ teams: nextTeams })
  return updated
}

// §unified-perms PR #3 (2026-05-23): change a team's category. When
// `resetToTemplate: true` is passed, the server also re-applies the
// category permission template — replacing the team's permissions[]
// with CATEGORY_PERMISSION_TEMPLATES[newCategory] and setting
// appliedTemplate = newCategory. When false, only `category` changes
// and `appliedTemplate = null` (= "Customized" badge).
//
// Both shapes ride the same `sdk.updateTeam` endpoint — the server
// reads `resetToTemplate` from the body and acts accordingly. Until
// the server endpoint understands this flag, the resetToTemplate=true
// path is a no-op on permissions (the category still changes). PR #4
// wires the server side.
export const updateTeamCategory = async (
  root,
  { orgId, teamId, category, resetToTemplate = false }
) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const res = await sdk.updateTeam(orgId, teamId, { category, resetToTemplate })
  const updated = res?.data || res
  const nextTeams = (root?.teams || []).map((t) =>
    t.id === teamId
      ? {
          ...t,
          category: updated?.category || category,
          permissions: updated?.permissions || t.permissions,
          appliedTemplate: updated?.appliedTemplate ?? null
        }
      : t
  )
  root.update({ teams: nextTeams })
  return updated
}

// Invite an email to join a team. On accept, the server creates (or
// re-uses) the user's OrgMember and adds them to the target team.
// Maps to POST /organizations/:orgId/teams/:teamId/invitations via the
// SDK's flat `inviteToTeam` method (alias of createTeamInvitation).
export const inviteToTeam = async (root, { orgId, teamId, email }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  if (!email) throw new Error('email required')
  return sdk.inviteToTeam(orgId, teamId, { email })
}

// Invite an email into the CURRENT WORKSPACE, optionally pre-attached to
// platform teams (GA-W2.1): the server resolves slugs/ids against the
// workspace's own org (fail-closed on unknown) and acceptance creates the
// WorkspaceMember + TeamMember rows atomically. Response carries
// { inviteUrl, emailSent } so the caller can always offer the copyable
// accept link (GA-W3.2).
export const inviteToWorkspace = async (root, { workspaceId, email, role, teams }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  if (!workspaceId) throw new Error('workspaceId required')
  if (!email) throw new Error('email required')
  return sdk.createWorkspaceInvitation(workspaceId, {
    email,
    role: role || 'editor',
    ...(Array.isArray(teams) && teams.length ? { teams } : {})
  })
}

// Return teams scoped to a single org — used by the team picker.
export const teamsInOrg = (orgId, root) => (root?.teams || []).filter((t) => t.orgId === orgId)

// True if the given email is a member of the active org. Used by
// consumers (filterPeople, mention lists) to scope the cross-tenant
// `public.people` view down to the current tenant.
export const isActiveOrgMember = (email, root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return false
  const set = root?.orgMembersByOrgId?.[orgId]
  if (!set) return false
  return !!set[String(email || '').toLowerCase()]
}
